import { z } from 'zod';

export const startTimeEntrySchema = z.object({
  projectId: z.string().uuid().nullable().optional(),
  taskId: z.string().uuid().nullable().optional(),
  description: z.string().max(500).default(''),
  tagIds: z.array(z.string().uuid()).default([]),
  billable: z.boolean().optional(),
  startedAt: z.string().datetime().optional(), // defaults to server "now"
});
export type StartTimeEntryInput = z.infer<typeof startTimeEntrySchema>;

export const stopTimeEntrySchema = z.object({
  stoppedAt: z.string().datetime().optional(), // defaults to server "now"
});
export type StopTimeEntryInput = z.infer<typeof stopTimeEntrySchema>;

export const createManualTimeEntrySchema = z
  .object({
    projectId: z.string().uuid().nullable().optional(),
    taskId: z.string().uuid().nullable().optional(),
    description: z.string().max(500).default(''),
    tagIds: z.array(z.string().uuid()).default([]),
    // Deliberately optional rather than `.default(false)`: the server needs to
    // tell "the caller said not billable" apart from "the caller said nothing",
    // because the latter inherits the project's billable flag.
    billable: z.boolean().optional(),
    startedAt: z.string().datetime(),
    stoppedAt: z.string().datetime(),
  })
  .refine((v: { startedAt: string; stoppedAt: string }) => new Date(v.stoppedAt) > new Date(v.startedAt), {
    message: 'stoppedAt must be after startedAt',
    path: ['stoppedAt'],
  });
export type CreateManualTimeEntryInput = z.infer<typeof createManualTimeEntrySchema>;

export const updateTimeEntrySchema = z.object({
  projectId: z.string().uuid().nullable().optional(),
  taskId: z.string().uuid().nullable().optional(),
  description: z.string().max(500).optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  billable: z.boolean().optional(),
  startedAt: z.string().datetime().optional(),
  stoppedAt: z.string().datetime().nullable().optional(),
});
export type UpdateTimeEntryInput = z.infer<typeof updateTimeEntrySchema>;

export const bulkUpdateTimeEntriesSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  projectId: z.string().uuid().nullable().optional(),
  taskId: z.string().uuid().nullable().optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  billable: z.boolean().optional(),
});
export type BulkUpdateTimeEntriesInput = z.infer<typeof bulkUpdateTimeEntriesSchema>;

export const listTimeEntriesQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  projectId: z.string().uuid().optional(),
  taskId: z.string().uuid().optional(),
  tagId: z.string().uuid().optional(),
  userId: z.string().uuid().optional(),
  billable: z.coerce.boolean().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListTimeEntriesQuery = z.infer<typeof listTimeEntriesQuerySchema>;

export const reportGroupBySchema = z.enum(['project', 'task', 'member', 'tag', 'day']);
export type ReportGroupBy = z.infer<typeof reportGroupBySchema>;

export const reportQuerySchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
  groupBy: reportGroupBySchema.default('project'),
  // Nested levels under `groupBy`, outermost first: `thenBy=tag` or
  // `thenBy=tag,day` (a repeated `thenBy` param works too). Dimensions that
  // repeat `groupBy` or each other are dropped by the service, not rejected.
  thenBy: z
    .preprocess(
      (value) => (typeof value === 'string' ? value.split(',').map((part) => part.trim()).filter(Boolean) : value),
      z.array(reportGroupBySchema).max(reportGroupBySchema.options.length - 1),
    )
    .optional(),
  projectId: z.string().uuid().optional(),
  memberId: z.string().uuid().optional(),
  tagId: z.string().uuid().optional(),
  billable: z.coerce.boolean().optional(),
});
export type ReportQuery = z.infer<typeof reportQuerySchema>;
